import { BBox, Feature, MultiPolygon, Position, Polygon } from 'geojson';
import * as mapboxgl from 'mapbox-gl/esm';
import { ExpressionSpecification, IControl, PointLike } from 'mapbox-gl/esm';

import { distance, point } from '@turf/turf';
import themeService from './theme.service';
import { getGlobalMaskLayer } from './utils.service';
import { safelyAddLayer, safelyAddSource, safelyRemoveLayer, safelyRemoveSource } from './maps/maps.utils';

export const getClusterColors = (): ExpressionSpecification => {
  const primary = themeService.getPropertyValue('--ion-color-primary') ?? '#51bbd6';
  const shade = themeService.getPropertyValue('--ion-color-primary-shade') ?? '#f1f075';
  const tint = themeService.getPropertyValue('--ion-color-primary-tint') ?? '#f28cb1';

  // TODO these step points (100 and 750) should be dependent on the current this.count
  return [
    'step',
    ['get', 'pc'],
    tint,
    100,
    shade,
    750,
    primary
  ];
}

export const getPrimaryColor = (): string => themeService.getPropertyValue('--ion-color-primary') ?? '#51bbd6';

export const MAP_CLUSTERS_LAYER_ID = 'MAP_CLUSTERS_LAYER_ID';
export const MAP_CLUSTERS_COUNT_LAYER_ID = 'MAP_CLUSTERS_COUNT_LAYER_ID';
export const MAP_CLUSTERS_POINT_LAYER_ID = 'MAP_CLUSTERS_POINT_LAYER_ID';
export const MAP_UNCLUSTERED_POINT_LAYER_ID = 'MAP_UNCLUSTERED_POINT_LAYER_ID';
export const MAP_UNCLUSTERED_POLYGON_LAYER_ID = 'MAP_UNCLUSTERED_POLYGON_LAYER_ID';
export const MAP_UNCLUSTERED_LINE_LAYER_ID = 'MAP_UNCLUSTERED_LINE_LAYER_ID';

const DEFAULT_POLYGON_OPACITY = 0.4;
const DEFAULT_LINE_OPACITY = 0.9;
const DEFAULT_POINT_OPACITY = 1;

export const MAP_DEFAULT_CENTER = [-2.5, 52.31];
export const MAP_DEFAULT_BOUNDS: BBox = [
  2.0153808593734936, 56.6877748258257,
  -7.0153808593762506, 47.45206245445874
];

export const buildOpacity = (opacity: number, ...expression: any[]): ExpressionSpecification => [
  'case',
  ...expression,
  ['boolean', ['feature-state', 'hidden'], false],
  0,
  opacity
];

export const defaultPolygonOpacity = buildOpacity(DEFAULT_POLYGON_OPACITY);
export const defaultLineOpacity = buildOpacity(DEFAULT_LINE_OPACITY);
export const defaultPointOpacity = buildOpacity(DEFAULT_POINT_OPACITY);

export const MAP_ALL_RECORDS_LAYERS = [
  MAP_CLUSTERS_LAYER_ID,
  MAP_CLUSTERS_COUNT_LAYER_ID,
  MAP_CLUSTERS_POINT_LAYER_ID,
  MAP_UNCLUSTERED_POINT_LAYER_ID,
  MAP_UNCLUSTERED_POLYGON_LAYER_ID,
  MAP_UNCLUSTERED_LINE_LAYER_ID
];

export const isMapStyleDark = (style: mapboxgl.StyleSpecification): boolean => {
  // Case-insensitive: the Bluesky style was once named "BlueSky Aerial".
  if (/^(bing|bluesky)/i.test(style.name ?? '')) {
    return true;
  }

  const darkMapboxStyles = ['Dark', 'Satellite', 'Satellite Streets'];
  if (style.name?.startsWith('Mapbox')) {
    const mapboxStyleName = style.name.substring(7);
    return darkMapboxStyles.includes(mapboxStyleName);
  }
  return false;
}

/** Minimum horizontal movement (meters) before we animate the location dot; below this we update immediately to avoid rAF every 2s when idle. */
const LOCATION_ANIMATION_MIN_DISTANCE_METERS = 1;

export class MapboxUserLocationControl implements IControl {

  marker: mapboxgl.Marker;
  userLocation: HTMLDivElement;
  private container: HTMLDivElement;
  private map: mapboxgl.Map;
  private currentPosition: Position | null = null;
  private animationFrameId: number | null = null;
  private animationDuration: number = 500;

  constructor(private position?: Position) { }

  onAdd(map: mapboxgl.Map) {
    this.map = map;
    const userLocation = document.createElement('div');
    userLocation.classList.add('mapboxgl-user-location');
    this.userLocation = userLocation;

    const userLocationDot = document.createElement('div');
    userLocationDot.classList.add('mapboxgl-user-location-dot')

    const userLocationHeading = document.createElement('div');
    userLocationHeading.classList.add('mapboxgl-user-location-heading');
    userLocation.appendChild(userLocationDot);
    userLocation.appendChild(userLocationHeading);

    this.marker = new mapboxgl.Marker({
      element: userLocation,
      rotationAlignment: 'map',
      pitchAlignment: 'map'
    });

    if (this.position) {
      this.setPosition(this.position);
    }

    this.container = document.createElement('div');
    return this.container;
  }

  setPosition(position: Position) {
    // If this is the first position, set it immediately
    if (!this.currentPosition) {
      this.currentPosition = position;
      this.marker.setLngLat([position[0], position[1]]).addTo(this.map);
      return;
    }

    // If movement is below threshold (e.g. GPS jitter when stationary), update immediately and skip animation to avoid rAF every 2s when idle
    const distMeters = distance(point(this.currentPosition), point(position), { units: 'meters' });
    if (distMeters < LOCATION_ANIMATION_MIN_DISTANCE_METERS) {
      if (this.animationFrameId !== null) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
      this.currentPosition = [position[0], position[1]];
      this.marker.setLngLat([position[0], position[1]]);
      return;
    }

    // Cancel any ongoing animation
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    const startPosition: Position = [...this.currentPosition] as Position;
    const targetPosition: Position = [...position] as Position;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / this.animationDuration, 1);

      // Ease-out function for smooth deceleration
      const eased = 1 - Math.pow(1 - progress, 3);

      // Interpolate between start and target positions
      const currentLng = startPosition[0] + (targetPosition[0] - startPosition[0]) * eased;
      const currentLat = startPosition[1] + (targetPosition[1] - startPosition[1]) * eased;

      this.marker.setLngLat([currentLng, currentLat]);

      if (progress < 1) {
        this.animationFrameId = requestAnimationFrame(animate);
      } else {
        // Animation complete
        this.currentPosition = targetPosition;
        this.animationFrameId = null;
      }
    };

    this.animationFrameId = requestAnimationFrame(animate);
  }

  setHeading(heading: number) {
    this.marker.setRotation(heading);
    this.userLocation.classList.add('mapboxgl-user-location-show-heading');
  }

  onRemove() {
    if(this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
    this.marker?.remove();
    this.container?.remove();
    this.map = null;
    return this;
  }
}

export const pointToBox = (point: mapboxgl.Point, boxSize: number): [PointLike, PointLike] => {
  return [
    [point.x - boxSize, point.y - boxSize],
    [point.x + boxSize, point.y + boxSize]
  ];
};

export const emptyStyle: mapboxgl.StyleSpecification = {
  version: 8,
  name: 'empty',
  metadata: {
    'mapbox:autocomposite': true,
    'mapbox:type': 'template'
  },
  glyphs: 'mapbox://fonts/mapbox/{fontstack}/{range}.pbf',
  sources: {},
  layers: [
    {
      id: 'background',
      type: 'background',
      paint: {
        'background-color': 'rgba(0,0,0,0)'
      }
    }
  ]
};

export class MapboxBoundsLayerControl implements IControl {
  private color: string;
  private lineColor: string;
  private data: Feature<Polygon | MultiPolygon>;
  private container: HTMLDivElement;
  private map: mapboxgl.Map;

  constructor(private bounds: MultiPolygon | Polygon) {
    this.data = getGlobalMaskLayer(bounds);
  }

  onAdd(map: mapboxgl.Map): HTMLElement {
    this.map = map;
    this.color = isMapStyleDark(map.getStyle()) ? '#fff' : '#000';
    this.lineColor = '#ff0000';

    safelyAddSource(map, 'appBoundsFillSource', {
      type: 'geojson',
      data: this.data
    });

    safelyAddSource(map, 'appBoundsLineSource', {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: this.bounds
      }
    });

    safelyAddLayer(map, {
      id: 'appBoundsFill',
      type: 'fill',
      source: 'appBoundsFillSource',
      paint: {
        'fill-opacity': 0.3,
        'fill-color': this.color
      }
    });

    safelyAddLayer(map, {
      id: 'appBoundsLine',
      type: 'line',
      source: 'appBoundsLineSource',
      paint: {
        'line-color': this.lineColor
      }
    });
    this.container = document.createElement('div');
    return this.container;
  }

  onRemove() {
    safelyRemoveLayer(this.map, 'appBoundsFill');
    safelyRemoveLayer(this.map, 'appBoundsLine');
    safelyRemoveSource(this.map, 'appBoundsFillSource');
    safelyRemoveSource(this.map, 'appBoundsLineSource');

    this.container.remove();
    this.map = null;
    return this;
  }
}
